import React, { useEffect, useState, Fragment } from 'react';
import { ClockIcon, AlertTriangleIcon } from 'lucide-react';
const STORAGE_KEY = 'pad_countdown_end_45m';
const COUNTDOWN_MINUTES = 45;
function getEndTime(): number {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored) {
    const end = parseInt(stored, 10);
    if (!isNaN(end) && end > Date.now()) return end;
  }
  const end = Date.now() + COUNTDOWN_MINUTES * 60 * 1000;
  localStorage.setItem(STORAGE_KEY, end.toString());
  return end;
}
function formatTime(ms: number): {
  hours: string;
  minutes: string;
  seconds: string;
} {
  if (ms <= 0) return {
    hours: '00',
    minutes: '00',
    seconds: '00'
  };
  const totalSeconds = Math.floor(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor(totalSeconds % 3600 / 60);
  const seconds = totalSeconds % 60;
  return {
    hours: hours.toString().padStart(2, '0'),
    minutes: minutes.toString().padStart(2, '0'),
    seconds: seconds.toString().padStart(2, '0')
  };
}
interface CountdownBarProps {
  variant?: 'banner' | 'inline';
}
export function CountdownBar({
  variant = 'banner'
}: CountdownBarProps) {
  const [remaining, setRemaining] = useState<number>(() => {
    const end = getEndTime();
    return Math.max(0, end - Date.now());
  });
  useEffect(() => {
    const interval = setInterval(() => {
      const end = getEndTime();
      const diff = Math.max(0, end - Date.now());
      setRemaining(diff);
      if (diff <= 0) clearInterval(interval);
    }, 1000);
    return () => clearInterval(interval);
  }, []);
  const {
    hours,
    minutes,
    seconds
  } = formatTime(remaining);
  const isExpired = remaining <= 0;
  const isLow = remaining > 0 && remaining < 30 * 60 * 1000;
  if (variant === 'inline') {
    return <div className="mb-5" data-id="element-81">
        <style data-id="element-82">{`
          @keyframes soft-pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.7; }
          }
          @keyframes gentle-glow {
            0%, 100% { box-shadow: 0 0 0 0 rgba(201,150,59,0.15); }
            50% { box-shadow: 0 0 12px 2px rgba(201,150,59,0.25); }
          }
        `}</style>
        {isExpired ? <div className="rounded-xl p-3.5 text-center border" style={{
        backgroundColor: 'var(--color-cream-warm)',
        borderColor: 'var(--color-border)'
      }} data-id="element-83">
            <span className="text-sm font-bold" style={{
          color: 'var(--color-deep-blue)'
        }} data-id="element-84">
              Esta oferta ya no está disponible
            </span>
          </div> : <div className="rounded-xl overflow-hidden border" style={{
        backgroundColor: 'var(--color-cream-warm)',
        borderColor: 'var(--color-gold)',
        animation: 'gentle-glow 3s ease-in-out infinite'
      }} data-id="element-85">
            <div className="px-4 py-3.5 text-center" data-id="element-86">
              {/* Urgency text */}
              <div className="flex items-center justify-center gap-1.5 mb-2.5" data-id="element-87">
                <ClockIcon className="w-4 h-4" style={{
              color: 'var(--color-gold)'
            }} data-id="element-88" />
                <span className="text-xs font-bold uppercase tracking-wide" style={{
              color: 'var(--color-gold)'
            }} data-id="element-89">
                  {isLow ? '¡Últimos minutos con este precio!' : 'Este precio especial termina pronto'}
                </span>
              </div>

              {/* Timer digits */}
              <div className="flex items-center justify-center gap-2.5" data-id="element-90">
                {[{
              value: hours,
              label: 'Hrs'
            }, {
              value: minutes,
              label: 'Min'
            }, {
              value: seconds,
              label: 'Seg'
            }].map((unit, i) => <Fragment key={i} data-id="element-91">
                    {i > 0 && <span className="text-lg font-bold -mx-0.5" style={{
                color: 'var(--color-gold)',
                animation: 'soft-pulse 1s ease-in-out infinite'
              }} data-id="element-92">
                        :
                      </span>}
                    <div className="flex flex-col items-center" data-id="element-93">
                      <div className="w-12 h-12 rounded-lg flex items-center justify-center" style={{
                  backgroundColor: 'var(--color-deep-blue)'
                }} data-id="element-94">
                        <span className="text-xl font-extrabold text-white tabular-nums" data-id="element-95">
                          {unit.value}
                        </span>
                      </div>
                      <span className="text-[10px] font-semibold mt-1" style={{
                  color: 'var(--color-body)'
                }} data-id="element-96">
                        {unit.label}
                      </span>
                    </div>
                  </Fragment>)}
              </div>

              {/* Subtle persuasion line */}
              <p className="text-[11px] font-medium mt-2.5" style={{
            color: 'var(--color-body)'
          }} data-id="element-97">
                {isLow ? 'Aprovechá antes de que termine' : 'Después de este tiempo, vuelve al precio original'}
              </p>
            </div>
          </div>}
      </div>;
  }
  return <div className="w-full py-2 text-center" style={{
    background: isExpired ? 'linear-gradient(90deg, #DC2626 0%, #B91C1C 100%)' : 'linear-gradient(90deg, #D97706 0%, #B45309 50%, #D97706 100%)',
    backgroundSize: '200% 100%',
    animation: isExpired ? 'none' : 'shimmer 3s ease-in-out infinite'
  }} data-id="element-98">
      <style data-id="element-99">{`
        @keyframes shimmer {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }
        @keyframes pulse-glow {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.85; }
        }
      `}</style>
      <div className="flex items-center justify-center gap-2" data-id="element-100">
        {isExpired ? <p className="text-sm font-bold text-white tracking-wide" data-id="element-101">
            ¡La oferta ha finalizado!
          </p> : <p className="text-sm font-bold text-white tracking-wide" data-id="element-102">
            🔥 Descuento especial termina en{' '}
            <span className="inline-flex gap-1 font-extrabold tabular-nums" style={{
          animation: 'pulse-glow 2s ease-in-out infinite'
        }} data-id="element-103">
              <span className="bg-white/20 rounded px-1.5 py-0.5" data-id="element-104">{hours}</span>
              <span data-id="element-105">:</span>
              <span className="bg-white/20 rounded px-1.5 py-0.5" data-id="element-106">
                {minutes}
              </span>
              <span data-id="element-107">:</span>
              <span className="bg-white/20 rounded px-1.5 py-0.5" data-id="element-108">
                {seconds}
              </span>
            </span>
          </p>}
      </div>
    </div>;
}